'use client';
import { upsellDismissedEvent, upsellViewedEvent, } from '#pplx/frontend/apps/ask/events/unified-upsell/analytics.js';
import { isCtaSupported } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/executeCtaHandler.js';
import { handleExternalUrl } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/handleExternalUrl.js';
import { useHomepageUpsellsQuery } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useHomepageUpsellsQuery.js';
import { getHomepageUpsellsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/upsellKeys.js';
import { markUpsellAsInteracted } from '#pplx/frontend/apps/ask/queries/upsell-queries/markUpsellAsInteracted.js';
import { useBannerUpsellAction } from '#pplx/frontend/apps/ask/upsell-slots/banner/internal/useBannerUpsellAction.js';
import { buildUpsellSlotAnalytics } from '#pplx/frontend/apps/ask/upsell-slots/UpsellSlotRenderPlan.js';
import { ICON_REFERENCE_TO_ICON_TYPE } from '#pplx/frontend/apps/ask/upsells-icons/icons.js';
import { EVENT_PAYLOAD, IMPRESSION_PAYLOAD, } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
// EXTERNAL_URL is scoped to the banner slot only — supported and executed here via
// customHandlers rather than registered globally, so it does not become an actionable
// CTA in other upsell slots (modal, side-card, sidebar, input, thread).
const BANNER_CUSTOM_HANDLERS = {
    EXTERNAL_URL: handleExternalUrl,
};
const BANNER_EXTRA_SUPPORTED_CTAS = Object.keys(BANNER_CUSTOM_HANDLERS);
const TERMS_PRIVACY_NOTICE_BANNER_NAME = 'terms_privacy_notice_banner';
const bannerUpsellDismissedEvent = EVENT_PAYLOAD;
const bannerUpsellViewedEvent = IMPRESSION_PAYLOAD;
const isHomepage = (pathname) => pathname === '/';
const isBannerUpsellCallsiteEnabled = (input) => input.callsite.enabled !== false;
const isBannerUpsellRequestEnabled = (input) => isBannerUpsellCallsiteEnabled(input) && input.callsite.surface?.hasPrimarySurfaceContent !== true;
const resolveBannerIcon = (upsellInformation) => {
    if (!upsellInformation?.icon_reference)
        return undefined;
    if (!(upsellInformation.icon_reference in ICON_REFERENCE_TO_ICON_TYPE))
        return undefined;
    return ICON_REFERENCE_TO_ICON_TYPE[upsellInformation.icon_reference];
};
const resolveBannerUpsellSlotPlan = ({ input, data, controls, closeLabel, }) => {
    const enabled = isBannerUpsellCallsiteEnabled(input);
    if (!enabled) {
        return { status: 'empty', show: false, reason: 'disabled' };
    }
    if (input.callsite.surface?.hasPrimarySurfaceContent) {
        return { status: 'blocked', show: false, reason: 'competing_surface' };
    }
    const { upsellInformation } = data;
    if (!upsellInformation) {
        return { status: 'empty', show: false, reason: 'no_backend_upsell' };
    }
    if (!upsellInformation.app_location ||
        !input.acceptedLocations.includes(upsellInformation.app_location)) {
        return { status: 'blocked', show: false, reason: 'location_mismatch' };
    }
    // Off-home: only render the org-invite upsell; block all other BANNER upsells so
    // promos stay home-page-only. The off-home request itself is gated to invite-holders
    // in AppBanner (resolveAppBannerPlan); this is the render-side counterpart. TEMPORARY
    // — collapse both into a placement-scoped backend query if a second off-home banner
    // is introduced.
    if (!isHomepage(input.callsite.pathname) && upsellInformation.name !== 'org_invitation') {
        return { status: 'blocked', show: false, reason: 'non_home_non_org_invite' };
    }
    if (upsellInformation.cta &&
        !isCtaSupported(upsellInformation.cta, BANNER_EXTRA_SUPPORTED_CTAS)) {
        return { status: 'blocked', show: false, reason: 'unsupported_cta' };
    }
    if (!upsellInformation.title || !upsellInformation.button_text) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    if (!data.show) {
        return { status: 'blocked', show: false, reason: 'dismissed' };
    }
    return {
        status: 'ready',
        show: true,
        slotId: input.slotId,
        upsellInformation,
        render: {
            kind: 'banner',
            componentKey: 'AppBannerUpsell',
            props: {
                icon: resolveBannerIcon(upsellInformation),
                title: upsellInformation.title,
                actionLabel: upsellInformation.button_text,
                onClick: controls.onClick,
                onDismiss: controls.onDismiss,
                closeLabel,
            },
        },
        actions: controls,
        analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
    };
};
export const useBannerUpsellSlotPlan = (input) => {
    const enabled = isBannerUpsellRequestEnabled(input);
    const { upsellInformation } = useHomepageUpsellsQuery({ enabled });
    const { track, impression } = useInstrumentation();
    const { $t } = useIntl();
    const queryClient = useQueryClient();
    const onClick = useBannerUpsellAction({
        upsellInformation: upsellInformation ?? undefined,
        options: { customHandlers: BANNER_CUSTOM_HANDLERS },
    });
    const [show, setShow] = useState(false);
    const onDismiss = useCallback(async () => {
        if (!upsellInformation)
            return;
        if (upsellInformation.upsell_type && upsellInformation.name) {
            track(bannerUpsellDismissedEvent({
                name: 'banner upsell dismissed',
                data: {
                    upsellName: upsellInformation.name,
                    upsellType: upsellInformation.upsell_type,
                    upsellUuid: upsellInformation.upsell_uuid,
                    eventMetadata: upsellInformation.event_metadata,
                },
            }));
            track(upsellDismissedEvent({
                name: 'upsell dismissed',
                data: {
                    upsellLocation: upsellInformation.app_location ?? input.slotId,
                    upsellUUID: upsellInformation.upsell_uuid,
                    upsellName: upsellInformation.name,
                    upsellType: upsellInformation.upsell_type,
                    eventMetadata: upsellInformation.event_metadata,
                },
            }));
        }
        setShow(false);
        await markUpsellAsInteracted({
            upsellName: upsellInformation.name,
            upsellUUID: upsellInformation.upsell_uuid,
            interactionType: 'dismiss',
        });
        // Refresh after the backend records the dismissal so every slot can receive
        // the next eligible upsell instead of retaining the session-long cached response.
        await queryClient.invalidateQueries({ queryKey: getHomepageUpsellsQueryKey() });
    }, [input.slotId, queryClient, track, upsellInformation]);
    useEffect(() => {
        if (!enabled || !upsellInformation)
            return;
        if (!upsellInformation.app_location ||
            !input.acceptedLocations.includes(upsellInformation.app_location))
            return;
        if (upsellInformation.cta &&
            !isCtaSupported(upsellInformation.cta, BANNER_EXTRA_SUPPORTED_CTAS))
            return;
        if (!isHomepage(input.callsite.pathname) && upsellInformation.name !== 'org_invitation') {
            return;
        }
        setShow(true);
        if (!upsellInformation.upsell_type || !upsellInformation.name) {
            return;
        }
        impression(bannerUpsellViewedEvent({
            name: 'banner upsell viewed',
            data: {
                upsellName: upsellInformation.name,
                upsellType: upsellInformation.upsell_type,
                upsellUuid: upsellInformation.upsell_uuid,
                eventMetadata: upsellInformation.event_metadata,
            },
        }));
        impression(upsellViewedEvent({
            name: 'upsell viewed',
            data: {
                upsellLocation: upsellInformation.app_location ?? input.slotId,
                upsellUUID: upsellInformation.upsell_uuid,
                upsellName: upsellInformation.name,
                upsellType: upsellInformation.upsell_type,
                eventMetadata: upsellInformation.event_metadata,
            },
        }));
    }, [
        enabled,
        input.callsite.pathname,
        upsellInformation,
        upsellInformation?.app_location,
        upsellInformation?.event_metadata,
        upsellInformation?.name,
        upsellInformation?.upsell_type,
        upsellInformation?.upsell_uuid,
        input.slotId,
        input.acceptedLocations,
        impression,
    ]);
    // Resolve a specific dismiss aria-label at the plan boundary so the generic
    // banner renderer stays independent of upsell identities. Other banners fall
    // back to NoticeBar's default "Close banner".
    const closeLabel = upsellInformation?.name === TERMS_PRIVACY_NOTICE_BANNER_NAME
        ? $t({
            defaultMessage: 'Dismiss privacy notice update',
            id: '1121465879',
            description: 'Accessible label for dismissing the Privacy Notice update banner',
        })
        : undefined;
    const plan = resolveBannerUpsellSlotPlan({
        input,
        data: {
            upsellInformation,
            show,
        },
        controls: {
            onDismiss,
            onClick,
        },
        closeLabel,
    });
    return plan;
};
//# sourceMappingURL=useBannerUpsellSlotPlan.js.map